<template>
  <div>
    <ValidationObserver @submit="receivedAssignmentHandler">
      <UiModalContainer
        footer-classes="flex justify-end gap-3"
        :modal-show="modal"
        @handleClick="handleClick"
      >
        <template v-slot:header>
          <span>
            <span v-if="activeRole === 'section_student'">Student Assignment Submit</span>
            <span v-else>Teacher Assignment Received</span>
          </span>
        </template>
        <div>
          <div>
            <Heading :heading="studentName" />
            <DetailCard :details="assignment" />
          </div>
          <UiMultiUploadFile
            v-model="fieldValue"
            :rules="imageRules"
            :is-multiple="isSingle"
            name="SUBMISSION ATTACHMENTS"
            title="SUBMISSION ATTACHMENTS"
            :is-file-loading="isFileLoading"
            :accept-format="settings.file_accept_format"
            @change="uploadAttachments"
            @removeFile="removeFile"
          />
          <UiInputBox
            v-model="description"
            type="textarea"
            name="Description"
            title="DESCRIPTION"
            class="mt-2"
            :rules="descriptionRules"
            placeholder="Write"
          />
        </div>
        <template v-slot:footer>
          <button
            class="focus:outline-none focus:shadow-md text-base text-white font-roboto h-8 flex justify-center items-center primary-button-outline"
            @click="emit('toggle')"
          >
            Cancel
          </button>
          <button
            class="focus:outline-none focus:shadow-md text-base text-white font-roboto h-8 flex justify-center items-center primary-button"
          >
            <span v-if="isLoading">
              <Loader />
            </span>
            <span v-else>
              <span v-if="activeRole === TENANT_ROLES.SECTION_STUDENT">Submit</span>
              <span v-else>Received</span>
            </span>
          </button>
        </template>
      </UiModalContainer>
    </ValidationObserver>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, defineEmits } from 'vue'

// Components
import UiModalContainer from '@components/UiElements/UiModalContainer.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import { Form as ValidationObserver } from 'vee-validate'
import DetailCard from '@src/components/UiElements/DetailCard.vue'
import UiMultiUploadFile from '@/src/components/UiElements/UiMultiUploadFile.vue'
import UiInputBox from '@src/components/UiElements/UiInputBox.vue'
import RULE_CONSTANTS from '@src/constants/rule.constants.js'
import Heading from '@/src/components/Heading.vue'
import { TENANT_ROLES } from '@src/constants/user-roles-constants.js'
// Store
import { useStore } from 'vuex'
const store = useStore()
const activeRole = computed(() => store.state.layout.activeRole)
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue.length) attachments.value = newValue
    emit('update:modelValue', attachments.value)
    fieldValue.value = attachments.value
  },
)
// Props
const props = defineProps({
  modal: {
    type: Boolean,
    default: false,
  },
  assignment: {
    type: Object,
    default: () => {},
  },
  id: {
    type: String,
    default: '',
    required: true,
  },
  studentName: {
    type: String,
    default: '',
  },
  settings: {
    type: Object,
    default: () => {},
  },
})

// Emits
const emit = defineEmits(['toggle', 'update:modelValue'])

// States
const isFileLoading = ref(false)
const fieldValue = ref([])
const description = ref('')
const attachments = ref([])
const isLoading = ref(false)
const errorFile = ref([])
const isSingle = computed(() => (props.settings.attachment_required === 'single' ? true : false))
// Computed
const imageRules = computed(() => {
  return `${RULE_CONSTANTS.SIZE}|${RULE_CONSTANTS.FILE_LIMIT}`
})
const descriptionRules = computed(() => {
  const rules = []

  if (props.settings.submission_description_required) {
    rules.push('required')
  }
  if (description.value) {
    rules.push('between:15,1000')
  }
  return rules.join('|')
})
const receivedAssignmentHandler = () => {
  isLoading.value = true
  const payload = {
    id: props.id,
    student_assignment_attatchments: fieldValue.value,
    submitted_description: description.value,
  }
  filterAssignmentStudents(payload)
    .then(() => {
      store.commit('toast/NEW', { message: 'Assignment Received Successfully', type: 'success' })
      handleClick('close', 'fetch')
    })
    .finally(() => {
      isLoading.value = false
    })
}

const handleClick = (eventName, payload) => {
  switch (eventName) {
    case 'close':
      emit('toggle', payload)
      break
  }
}
watch(
  () => attachments.value,
  () => attachmentsHandler(),
)

// Mounted hook
onMounted(() => {
  attachmentsHandler()
})
const uploadAttachments = async (file) => {
  isLoading.value = true
  errorFile.value = []
  if (isSingle.value) {
    attachments.value = []
    fieldValue.value = []
  }
  isFileLoading.value = true
  const form = new FormData()
  for (let i = 0; i < file.length; i++) {
    if (isFileNameUnique(file[i].name) && getSizeInKB(file[i].size) <= 500) {
      form.append(`assignment_attachment[${i}]`, file[i])
    } else {
      errorFile.value.push(file[i])
    }
  }
  const formDataEntries = Array.from(form.entries())
  if (formDataEntries.length) {
    uploadAssignmentAttachments(form)
      .then((response) => {
        let attachment = response.data
        for (let i = 0; i < attachment.length; i++) {
          attachments.value.push(attachment[i])
        }
      })
      .finally(() => {
        attachments.value.push(...errorFile.value)
        fieldValue.value = attachments.value
        isFileLoading.value = false
        isLoading.value = false
      })
  } else {
    setTimeout(() => {
      attachments.value.push(...errorFile.value)
      fieldValue.value = attachments.value
      isFileLoading.value = false
      isLoading.value = false
    }, 500)
  }
}
const attachmentsHandler = () => {
  emit('update:modelValue', attachments.value)
}
const isFileNameUnique = (fileName) => {
  return !fieldValue.value?.some((file) => file && (file.name || file.file_name) === fileName)
}
const getSizeInKB = (size) => {
  return (size / 1000).toFixed(2)
}
const removeFile = (index) => {
  attachments.value = fieldValue.value
}

// Actions
const filterAssignmentStudents = (payload) =>
  store.dispatch('assignment/receiveAssignmentStudent', payload)
const uploadAssignmentAttachments = (payload) =>
  store.dispatch('assignment/uploadAssignmentAttachments', payload)
</script>
